import type { Room } from '@brush/api-client';
import { useActingRole } from '@brush/auth';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { pinnedRoomId } from './authConfig';
import { clearDefaultRoomId, loadDefaultRoomId, saveDefaultRoomId } from './preferences';
import { useCurrentUser, useRooms } from './queries';
import { roleChoicesFor } from './roles';
import { roleInForce } from './roleInForce';

interface RoomContextValue {
  room: Room | null;
  rooms: Room[];
  roomId: string | null;
  setRoomId: (id: string) => void;
  /**
   * The room this operator has chosen to open on, or null for "whichever comes first". Kept here
   * rather than read from storage where it is shown, so the pane that sets it and the resolution
   * below it can never disagree about what is stored.
   */
  defaultRoomId: string | null;
  setDefaultRoomId: (id: string | null) => void;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

const RoomContext = createContext<RoomContextValue | null>(null);

export function useRoom(): RoomContextValue {
  const ctx = useContext(RoomContext);
  if (!ctx) throw new Error('useRoom must be used within a RoomProvider.');
  return ctx;
}

export function RoomProvider({ children }: { children: ReactNode }) {
  const meQuery = useCurrentUser();
  const { actingRole } = useActingRole();
  // Not until a role is in force: behind the role chooser the list would be read as nobody, then again once a
  // role is chosen and the cache cleared (#544, the rooms half of known gap 70).
  const offerable = meQuery.data ? roleChoicesFor(meQuery.data.availableRoles).length : null;
  const roomsQuery = useRooms(roleInForce(offerable, actingRole));
  const [selectedId, setSelectedId] = useState<string | null>(pinnedRoomId || null);
  // Read once, at mount. This is the room the app *opens* on: re-reading storage later would let
  // a change made in another tab move the room out from under whatever is on screen here.
  const [defaultRoomId, setStoredDefault] = useState<string | null>(() => loadDefaultRoomId());

  // Operators see only the rooms assigned to them; administrators see every room. The server
  // already scopes GET /api/rooms to the caller (known gap 30, closed), so this filter is a second
  // opinion rather than the control — kept because /api/me's roomIds is also what decides the
  // room the app opens on, and the two agreeing is cheap to check.
  const operatedIds = meQuery.data?.roomIds;
  const allRooms = roomsQuery.data ?? [];
  const rooms = operatedIds ? allRooms.filter((r) => operatedIds.includes(r.id)) : allRooms;

  // The saved default is only honoured while it names a room this operator can still see: an
  // assignment can be withdrawn, and a preference pointing at a room that is no longer in the
  // list would otherwise open the app on nothing at all.
  const preferredId =
    defaultRoomId !== null && rooms.some((r) => r.id === defaultRoomId) ? defaultRoomId : null;

  // Resolve the active room: explicit selection > pinned env > saved default > first room. The
  // env pin comes first because it is a build-time pin to one room (dev only, and empty in every
  // checked-in .env), so where it is set at all it is meant to hold.
  const roomId = selectedId ?? preferredId ?? rooms[0]?.id ?? null;
  const room = rooms.find((r) => r.id === roomId) ?? null;

  const setDefaultRoomId = useCallback((id: string | null) => {
    if (id === null) clearDefaultRoomId();
    else saveDefaultRoomId(id);
    setStoredDefault(id);
  }, []);

  const value = useMemo<RoomContextValue>(
    () => ({
      room,
      rooms,
      roomId,
      setRoomId: setSelectedId,
      defaultRoomId,
      setDefaultRoomId,
      isLoading: roomsQuery.isLoading || meQuery.isLoading,
      isError: roomsQuery.isError || meQuery.isError,
      refetch: () => {
        void roomsQuery.refetch();
        void meQuery.refetch();
      },
    }),
    [room, rooms, roomId, defaultRoomId, setDefaultRoomId, roomsQuery, meQuery],
  );

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
}
